import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { AcAdMenuItem } from "@/lib/assets";

interface MenuContextType {
  menuItems: AcAdMenuItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isRefetching: boolean;
}

const MenuContext = createContext<MenuContextType | null>(null);

export function useMenu(): MenuContextType {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error("useMenu must be used within a MenuProvider");
  }
  return context;
}

type WordpressMenuProviderProps = {
  children: React.ReactNode;
  menu_id?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export function WordpressMenuProvider({
  children,
  menu_id = "primary",
  ...divProps
}: WordpressMenuProviderProps) {
  const initialItems = window.ACAD_REACT_CONFIG?.menuItems || [];
  const [menuItems, setMenuItems] = useState<AcAdMenuItem[]>(initialItems);
  const [error] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);

  const refetch = useCallback(async () => {
    setIsRefetching(true);
    setMenuItems(window.ACAD_REACT_CONFIG?.menuItems || []);
    setIsRefetching(false);
  }, []);

  const value = useMemo<MenuContextType>(
    () => ({
      menuItems,
      loading: false,
      error,
      refetch,
      isRefetching,
    }),
    [error, isRefetching, menuItems, refetch],
  );

  return (
    <MenuContext.Provider value={value}>
      <div {...divProps} data-acad-menu-id={menu_id}>
        {children}
      </div>
    </MenuContext.Provider>
  );
}

export const MenuProvider = WordpressMenuProvider;
export type MenuItem = AcAdMenuItem;
